import { setMain, portalNav } from "../components/shell";
import { login, requireAdmin, getSession } from "../lib/auth";
import { navigate, currentPath } from "../lib/router";
import { site, races, membersSeed, formatGBP, formatDate } from "../lib/content";

const adminLinks = [
  { href: "/admin", label: "Overview" },
  { href: "/admin/members", label: "Members" },
  { href: "/admin/updates", label: "Updates CMS" },
  { href: "/admin/calendar", label: "Race calendar" },
  { href: "/admin/finance", label: "Finance" },
];

function gate(): boolean {
  const s = requireAdmin();
  if (!s) {
    navigate("/admin/login", true);
    return false;
  }
  return true;
}

function wrap(title: string, body: string): void {
  const path = currentPath();
  const session = getSession()!;
  setMain(`
    <section class="page-hero" style="padding-bottom:0.5rem">
      <div class="container">
        <div class="kicker" style="color:var(--cg-green-700)">Admin · demo</div>
        <h1>${title}</h1>
        <p class="lede">Signed in as <strong>${session.name}</strong> — stub tools only</p>
        ${portalNav(adminLinks, path)}
      </div>
    </section>
    <section class="section section--tight">
      <div class="container">${body}</div>
    </section>
  `);
  document.title = `${title} — Admin — ${site.club.name}`;
}

export function pageAdminLogin(): void {
  if (requireAdmin()) {
    navigate("/admin", true);
    return;
  }
  setMain(`
    <section class="section">
      <div class="container" style="max-width:28rem;margin:2rem auto">
        <div class="card form">
          <div class="kicker" style="color:var(--cg-green-700)">Admin</div>
          <h1 style="font-size:2rem">Admin login</h1>
          <p style="color:var(--cg-muted)">Hardcoded demo user. Session stored in localStorage.</p>
          <div class="callout" style="font-size:0.9rem">
            <strong>Demo:</strong> admin@pieceoftheaction.demo / admin123
          </div>
          <form id="admin-login" class="form">
            <div class="field">
              <label for="email">Email</label>
              <input id="email" name="email" type="email" required value="admin@pieceoftheaction.demo" />
            </div>
            <div class="field">
              <label for="password">Password</label>
              <input id="password" name="password" type="password" required value="admin123" />
            </div>
            <button class="btn btn--primary btn--block" type="submit">Sign in</button>
            <div id="login-msg" hidden></div>
          </form>
        </div>
      </div>
    </section>
  `);
  document.title = `Admin login — ${site.club.name}`;
  document.getElementById("admin-login")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const user = login(String(fd.get("email")), String(fd.get("password")));
    const box = document.getElementById("login-msg")!;
    if (!user || user.role !== "admin") {
      box.hidden = false;
      box.className = "form-msg form-msg--err";
      box.textContent = "Admin demo credentials required.";
      return;
    }
    navigate("/admin");
  });
}

export function pageAdminOverview(): void {
  if (!gate()) return;
  const active = membersSeed.membersList.filter((m) => m.status === "active").length;
  const revenue = membersSeed.membersList.filter((m) => m.status === "active").reduce((s, m) => s + Number((m as { pcm?: number }).pcm ?? 0), 0);
  wrap(
    "Overview",
    `
    <div class="stat-grid" style="margin-bottom:1.5rem">
      <div class="stat"><div class="stat__label">Members (seed)</div><div class="stat__value">${membersSeed.membersList.length}</div></div>
      <div class="stat"><div class="stat__label">Active</div><div class="stat__value">${active}</div></div>
      <div class="stat"><div class="stat__label">Fee</div><div class="stat__value" style="font-size:1.5rem">${formatGBP(150)}</div></div>
      <div class="stat"><div class="stat__label">Indicative income</div><div class="stat__value" style="font-size:1.5rem">${formatGBP(revenue)}</div></div>
    </div>
    <div class="grid-2">
      <div class="card">
        <h3>Ops checklist (stub)</h3>
        <ul class="list-check">
          <li>Von Trotter Syndicate horse profile published (demo)</li>
          <li>Publish first yard visit date</li>
          <li>Wire real auth before go-live</li>
          <li>Solicitor review of Terms / Privacy</li>
        </ul>
      </div>
      <div class="card">
        <h3>Product reminders</h3>
        <div class="callout">Syndicate shares · prize money annually by % · returns not guaranteed · apply not live pay</div>
        <p style="margin-top:1rem;color:var(--cg-muted)">Indicative figure uses seed members × their pcm (stub). Not live accounting.</p>
      </div>
    </div>
  `
  );
}

export function pageAdminMembers(): void {
  if (!gate()) return;
  wrap(
    "Members list",
    `
    <p>Seed list for demo. No create/edit API in v1.</p>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr><th>Name</th><th>Email</th><th>Joined</th><th>Status</th><th>pcm</th></tr>
        </thead>
        <tbody>
          ${membersSeed.membersList
            .map(
              (m) => `
            <tr>
              <td>${m.name}</td>
              <td>${m.email}</td>
              <td>${formatDate(m.joined)}</td>
              <td><span class="pill ${m.status === "active" ? "pill--green" : "pill--warn"}">${m.status}</span></td>
              <td>${formatGBP((m as {pcm?: number}).pcm ?? 150)}</td>
            </tr>`
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `
  );
}

export function pageAdminUpdates(): void {
  if (!gate()) return;
  wrap(
    "Updates CMS",
    `
    <p>Stub CMS — composing here saves to <code>localStorage</code> only.</p>
    <div class="two-col">
      <form class="card form" id="update-form">
        <div class="field">
          <label for="title">Title</label>
          <input id="title" name="title" required />
        </div>
        <div class="field">
          <label for="body">Body</label>
          <textarea id="body" name="body" required></textarea>
        </div>
        <button class="btn btn--primary" type="submit">Publish to local stub</button>
        <div id="upd-msg" hidden></div>
      </form>
      <div>
        <h3>Published (seed + local)</h3>
        <div class="timeline" id="upd-list">
          ${membersSeed.updates
            .map(
              (u) => `
            <article class="card timeline-item">
              <div class="timeline-item__date">${formatDate(u.date)}</div>
              <h3 style="margin:0.25rem 0;font-size:1.2rem">${u.title}</h3>
              <p style="margin:0;color:var(--cg-muted)">${u.body}</p>
            </article>`
            )
            .join("")}
        </div>
      </div>
    </div>
  `
  );
  const key = "pieceoftheaction.admin.updates";
  const renderLocal = () => {
    const local = JSON.parse(localStorage.getItem(key) || "[]") as Array<{
      title: string;
      body: string;
      date: string;
    }>;
    const list = document.getElementById("upd-list");
    if (!list || !local.length) return;
    const extra = local
      .map(
        (u) => `
      <article class="card timeline-item">
        <div class="timeline-item__date">${formatDate(u.date)} · local</div>
        <h3 style="margin:0.25rem 0;font-size:1.2rem">${u.title}</h3>
        <p style="margin:0;color:var(--cg-muted)">${u.body}</p>
      </article>`
      )
      .join("");
    list.insertAdjacentHTML("afterbegin", extra);
  };
  renderLocal();
  document.getElementById("update-form")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const item = {
      title: String(fd.get("title")),
      body: String(fd.get("body")),
      date: new Date().toISOString().slice(0, 10),
    };
    const prev = JSON.parse(localStorage.getItem(key) || "[]");
    prev.unshift(item);
    localStorage.setItem(key, JSON.stringify(prev));
    const box = document.getElementById("upd-msg")!;
    box.hidden = false;
    box.className = "form-msg form-msg--ok";
    box.textContent = "Saved locally. Reload shows it above the seed posts.";
    (e.target as HTMLFormElement).reset();
  });
}

export function pageAdminCalendar(): void {
  if (!gate()) return;
  wrap(
    "Race calendar — Von Trotter",
    `
    <p>Career results diary for the Syndicate horse (demo). Edit seed data in <code>content/races.json</code>. Future entries TBA — not invented.</p>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr><th>Date</th><th>Course</th><th>Race</th><th>Pos</th><th>SP</th></tr>
        </thead>
        <tbody>
          ${races.entries
            .map((r) => {
              const row = r as { date: string; course: string; race: string; pos?: string; sp?: string; status: string };
              return `
            <tr>
              <td>${formatDate(row.date)}</td>
              <td>${row.course}</td>
              <td>${row.race}</td>
              <td>${row.pos ?? row.status}</td>
              <td>${row.sp ?? "—"}</td>
            </tr>`;
            })
            .join("")}
        </tbody>
      </table>
    </div>
  `
  );
}

export function pageAdminFinance(): void {
  if (!gate()) return;
  const members = membersSeed.membersList;
  const active = members.filter((m) => m.status === "active");
  const pending = members.filter((m) => m.status === "pending");
  const fee = 150; // indicative 5% tier pcm for stub maths
  const gross = active.length * fee;
  wrap(
    "Finance placeholder",
    `
    <div class="callout" style="margin-bottom:1.25rem">
      Simple model: <strong>N active members × ${formatGBP(fee)}</strong>. Not bookkeeping, not VAT treatment, not live payments.
    </div>
    <div class="stat-grid" style="margin-bottom:1.5rem">
      <div class="stat"><div class="stat__label">Active members (N)</div><div class="stat__value">${active.length}</div></div>
      <div class="stat"><div class="stat__label">Pending</div><div class="stat__value">${pending.length}</div></div>
      <div class="stat"><div class="stat__label">Fee</div><div class="stat__value" style="font-size:1.5rem">${formatGBP(fee)}</div></div>
      <div class="stat"><div class="stat__label">N × fee</div><div class="stat__value" style="font-size:1.5rem">${formatGBP(gross)}</div></div>
    </div>
    <div class="card">
      <h3>Formula</h3>
      <p style="font-family:var(--font-display);font-size:1.5rem;margin:0">
        ${active.length} × ${formatGBP(fee)} = <strong>${formatGBP(gross)}</strong>
      </p>
      <p style="color:var(--cg-muted);margin-top:0.75rem">If pending convert: ${(active.length + pending.length)} × ${formatGBP(fee)} = ${formatGBP((active.length + pending.length) * fee)}</p>
    </div>
  `
  );
}
